// socialtie.com, signed out — the public front door.
//
// This is the ONLY view in the app that renders for a visitor with no session
// and no email link, so it carries its own header rather than the app chrome:
// `chrome.ts` deliberately hides the nav for a signed-out visitor, because a nav
// whose every link bounces to /login is worse than no nav at all. That rule is
// right and stays; this page just brings its own.
//
// Deliberately NOT a feature list. The people who land here are Will's own
// contacts arriving from a link or a card, and the question they have is "what
// is this and why am I here", not "how does it compare". Three plain sections.

import { el } from '../dom.js';
import type { View } from '../router.js';

/** One capability, stated as what it does for you rather than what it is. */
interface Pitch {
  title: string;
  body: string;
}

const PITCHES: Pitch[] = [
  {
    title: 'One place for everyone you know',
    body:
      'Names, numbers, addresses and the small things you would otherwise forget. '
      + 'Organised by how you actually know someone, not by which app you met them in.',
  },
  {
    title: 'Seven kinds of relationship',
    body:
      'Family, Friends, Communal, Business, School, Theological, Activities. '
      + 'You choose what each group of people can see about you: your home address '
      + 'to family, your work details to colleagues.',
  },
  {
    title: 'A family tree you build together',
    body:
      'Not a genealogy file that lives on one computer. You hold one branch, your '
      + 'aunt holds another, and it stays current because the family maintains it.',
  },
];

export const socialtieHomeView: View = (outlet) => {
  const header = el('header', { class: 'st-landing-header' }, [
    el('div', { class: 'st-landing-header-inner' }, [
      el('img', { class: 'st-landing-logo', src: '/socialtie-logo.png', alt: 'SocialTie' }),
      el('nav', { class: 'st-landing-nav' }, [
        el('a', { class: 'btn btn-primary', href: '/login' }, 'Log in'),
      ]),
    ]),
  ]);

  const hero = el('section', { class: 'st-hero' }, [
    el('h1', { class: 'st-hero-title' }, 'Fixing chaos one person, place and event at a time'),
    el(
      'p',
      { class: 'st-hero-lead' },
      'SocialTie is a personal address book for the people who actually matter to you: '
      + 'and a shared family tree the whole family keeps up to date.',
    ),
    el('div', { class: 'st-hero-actions' }, [
      el('a', { class: 'btn btn-primary', href: '/login' }, 'Log in'),
      el('a', { class: 'btn', href: 'https://mysocialcal.com' }, 'Looking for MySocialCal?'),
    ]),
  ]);

  const pitches = el(
    'section',
    { class: 'st-pitches' },
    PITCHES.map((p) =>
      el('div', { class: 'st-pitch' }, [
        el('h2', { class: 'st-pitch-title' }, p.title),
        el('p', { class: 'st-pitch-body' }, p.body),
      ]),
    ),
  );

  // Registration is closed on the PHP site and stays closed here: there is no
  // public sign-up, and saying so plainly beats a Create Account button that
  // dead-ends. See docs/domain.md on invites.
  const closing = el('section', { class: 'st-closing' }, [
    el('h2', {}, 'Accounts are by invitation'),
    el(
      'p',
      {},
      'SocialTie is not open for public sign-up. If someone has invited you, the link '
      + 'in your email will get you in.',
    ),
  ]);

  const footer = el('footer', { class: 'site-footer' }, [
    el('div', { class: 'site-footer-inner' }, [
      el('a', { href: '/privacy' }, 'Privacy Policy'),
      el('a', { href: '/terms' }, 'Terms and Conditions'),
      el('a', { href: '/contact' }, 'Contact us'),
    ]),
  ]);

  outlet.append(header, el('main', { class: 'st-landing' }, [hero, pitches, closing]), footer);
};
